import React, { useState, useEffect, useRef } from 'react';
import { useSelectiveClickOutside } from '../hooks/useSelectiveOutsideClick';

const RenderText = ({ text }) => {
  const textContent =
    typeof text === "string"
      ? { type: "string", value: text }
      : text && typeof text === "object"
        ? { type: "parts", bold: text.bold ?? "", normal: text.normal ?? "" }
        : { type: "string", value: "" };

  return textContent.type === "string" ? (
    <>{textContent.value}</>
  ) : (
    <>
      {textContent.bold && <b>{textContent.bold} </b>}
      {textContent.normal}
    </>
  );
};

/**
 * InfoWidget Component
 * An interactive info element that transitions from a circle to a rounded rectangle.
 * 
 * Props:
  * @param {string | { bold: string, normal: string }} text
 *        The text to display inside the widget.
 *        Either a plain string or an object with `bold` and `normal` parts.
 * @param {number} timer - Time in ms before auto-closing (default: 5000)
 * @param {boolean} isOpen - Initial open state (default: false)
 * @param {boolean} hoverable - Whether hover behavior applies (default: true)
 * @param {number} speed - Transition speed in ms (default: 300)
 * @param {string} className - Additional CSS classes for positioning
 * @param {object} style - Additional inline styles
 */
const InfoWidget = ({ 
  text = "Mit einem Klick auf die jeweiligen Module erfahren Sie mehr über deren Funktionen und Einsatzmöglichkeiten.", 
  timer = 5000, 
  isOpen = false, 
  hoverable = true,
  speed = 300,
  className = "",
  style = {}
}) => {
  const [open, setOpen] = useState(isOpen);
  const [isManual, setIsManual] = useState(false); // Tracks if state is manually locked (click)
  const timeoutRef = useRef(null);
  const elementRef = useRef(null);
  
  // Transition style
  const transitionStyle = {
    transitionDuration: `${speed}ms`,
    ...style
  };

  // Function to clear existing timeout
  const clearAutoCloseTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Function to start auto-close timer
  const startAutoCloseTimer = () => {
    clearAutoCloseTimer();
    timeoutRef.current = setTimeout(() => {
        setOpen(false);
    }, timer);
  };

  // Close when clicking outside
  useSelectiveClickOutside({
    enabled: open,
    containerRef: elementRef,
    onOutside: () => setOpen(false)
  });

  // Handle intersection (appearing on screen)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only trigger initial open if it hasn't been manually interacted with yet
        if (entry.isIntersecting && !isManual) {
           setOpen(true);
           startAutoCloseTimer();
           // Disconnect after first trigger to avoid loops
           observer.disconnect();
        }
      },
      { threshold: 0.5 } // Trigger when 50% visible
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      observer.disconnect();
      clearAutoCloseTimer();
    };
  }, [timer, isManual]);

  // Handlers
  const handleMouseEnter = () => {
    if (!hoverable) return;
    
    // Always pause timer on hover
    clearAutoCloseTimer();
    
    // If closed, open it and reset manual lock (so it auto-closes on leave)
    if (!open) {
      setOpen(true);
      setIsManual(false); 
    }
    // If open and isManual, we simply pause (already done) and stay open.
    // We do NOT reset isManual if it was already open and locked.
  };

  const handleMouseLeave = () => {
    if (!hoverable) return;
    
    // If not manually locked, restart auto-close timer
    if (!isManual) {
        startAutoCloseTimer();
    }
  };

  const handleClick = () => {
    // Toggle state and lock manual mode
    // "When clicked, it opens and cloases directly without any timeout."
    setOpen(prev => !prev);
    setIsManual(true);
    clearAutoCloseTimer(); 
  };

  return (
    <div 
      ref={elementRef}
      className={`info-widget-container ${open ? 'is-open' : ''} ${className}`}
      style={transitionStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      role="button"
      aria-expanded={open}
      aria-label="Information widget"
    >
      {/* Icon Area (Left anchor) */}
      <div className="info-widget-icon-wrapper">
        <div className="info-widget-outer-circle">
          <div className="info-widget-inner-circle">
            <span className="info-widget-letter">i</span>
          </div>
        </div>
      </div>

      {/* Text Area (Revealed on open) */}
      <div className="info-widget-text">
        <p>
          <RenderText text={text} />
        </p>
      </div>
    </div>
  );
};

export default InfoWidget;
